import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { HTTPError } from 'ky';

import { useWmgSession } from '~/modules/auth/hooks/useWmgSession';
import {
  LabelGroupQueryKeys,
  LabelQueryKeys,
} from '~settings/api/config/queryKeys';
import { BFFClient } from '~settings/api/lib/BFFClient';
import { getRequestHeaders } from '~settings/api/lib/headers';
import type { LabelDetail } from '~settings/api/lib/schemas';
import type { LabelDetailPayload } from '~settings/api/lib/types';

export const useLabelEdit = (
  labelId: string,
  options?: UseMutationOptions<LabelDetail, HTTPError, LabelDetailPayload>,
) => {
  const { token } = useWmgSession();
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation<LabelDetail, HTTPError, LabelDetailPayload>({
    mutationFn: async (labelData) => {
      return BFFClient.patch(`labels/${labelId}`, {
        json: labelData,
        headers: getRequestHeaders(token),
      }).json();
    },

    onSuccess: async (data, variables, ctx) => {
      queryClient.setQueryData(LabelQueryKeys.detail({ labelId }), data);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: LabelGroupQueryKeys.list({}),
        }),
        queryClient.invalidateQueries({
          queryKey: LabelQueryKeys.list({}),
        }),
      ]);

      if (onSuccess) {
        onSuccess(data, variables, ctx);
      }
    },
    onError: (error) => {
      console.error('Edit label failed:', error);
    },
    ...restOptions,
  });
};
